// ENG-4873 (N2): record a failed analytics start in error tracking.
//
// When posthog-js fails to start, PostHog's own error tracking is unavailable
// by definition and the client has no other error service, so a failed start
// would otherwise look exactly like "no activity". This module is the one
// place that turns such a failure into a traceable record:
//
//  * Every failure mints a fresh `incident_id` and writes ONE console line
//    carrying the error message, the product, the app version and that id.
//  * Desktop: the same line is forwarded to the Electron main process over the
//    existing `forwardLog` IPC bridge, so it lands in the session log that main
//    ships to the telemetry service every five minutes. That line is the record.
//    `forwardLog` is a no-op outside Electron, so the browser build is unaffected.
//  * Web: there is no sink today (decided Oct 28). The console line stands, and
//    the PostHog start is retried ONCE after 30 seconds. If the retry succeeds a
//    single `analytics_start_failed` event carrying the `incident_id` is sent so
//    the gap becomes visible in PostHog after all. If it fails too, the gap stays
//    open and the second line repeats the same id so both are joinable.
//
// Nothing is shown on screen and there is no switch: there is nothing to turn off.
// Later analytics calls already no-op without throwing because PostHogManager
// guards every SDK call on a null instance (covered by manager.start-failure.test.ts).
//
// Per rule: think-in-systems (ai.knowledge/global) — reuses the forwardLog
// primitive and the manager's capture path; no new IPC channel, no new sink.
// Per rule: reference-rules-in-comments (ai.knowledge/global).

import type { PostHog } from 'posthog-js';
import { COMMON_PROPERTIES, ENVELOPE_CLIENT, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveBuildVersion } from '#~/analytics/utils/build-version.js';
import { config } from '#~/config/config.js';
import { isElectronApp } from '#~/electron/utils.js';
import { forwardLog } from '#~/logger/forward.js';
import { PostHogManager } from './manager.js';

/** The ticket's single web retry: one attempt, 30 seconds after the failure. */
export const ANALYTICS_START_RETRY_DELAY_MS = 30_000;

/** `[source]` prefix main writes ahead of the forwarded line; grep target. */
export const ANALYTICS_START_FAILURE_SOURCE = 'posthog';

/** Every line this module writes starts with this, so one grep finds them all. */
export const ANALYTICS_START_FAILED_LINE_PREFIX = 'analytics start failed';

export type AnalyticsStartFailureRecord = {
  readonly incidentId: string;
  /** The exact single line written to the console and, on desktop, to the session log. */
  readonly line: string;
};

type AnalyticsStartFailureOptions = {
  /** Whatever `initializeSync` threw. */
  readonly error: unknown;
  /** Re-runs the same start the first attempt made. Web only; desktop never retries. */
  readonly retry?: () => PostHog;
  /** Test seam for the retry timer; defaults to the global setTimeout. */
  readonly schedule?: (callback: () => void, delayMs: number) => unknown;
};

const mintIncidentId = (): string => {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }
  // Fallback for environments without randomUUID: still unique enough to trace one
  // failure through its console line, session log line and retry event.
  return `incident-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// The thrown value is usually PostHogInitializationError wrapping the SDK's own
// error; the cause is the message a support engineer needs, so include both.
// Collapsed onto one line because the session log is line-oriented.
const describeError = (error: unknown): string => {
  if (error instanceof Error) {
    const cause = error.cause instanceof Error ? error.cause.message : undefined;
    const text = cause && cause !== error.message ? `${error.message}: ${cause}` : error.message;
    return text.replace(/\s+/g, ' ').trim();
  }
  return String(error).replace(/\s+/g, ' ').trim();
};

const describeFailure = (incidentId: string, error: unknown): string => {
  const client = isElectronApp() ? ENVELOPE_CLIENT.ELECTRON : ENVELOPE_CLIENT.STANDALONE_WEB;
  // Same product and build vocabulary the event envelope uses (`vendor`,
  // `app_version`), so the log line and PostHog rows name the same things.
  return [
    ANALYTICS_START_FAILED_LINE_PREFIX,
    `${COMMON_PROPERTIES.INCIDENT_ID}=${incidentId}`,
    `product=${config.vendor}`,
    `${COMMON_PROPERTIES.APP_VERSION}=${resolveBuildVersion(config.appVersion)}`,
    `${COMMON_PROPERTIES.CLIENT}=${client}`,
    `error=${describeError(error)}`
  ].join(' ');
};

/**
 * Records one failed analytics start and, on web, arms the single retry.
 *
 * Never throws: a failure to record a failure must not make startup worse.
 */
export function recordAnalyticsStartFailure(
  options: AnalyticsStartFailureOptions
): AnalyticsStartFailureRecord {
  const incidentId = mintIncidentId();
  const line = describeFailure(incidentId, options.error);

  console.error(`[PostHog] ${line}`);
  // Desktop sink: main prefixes `[posthog]` and writes the line to the session
  // log (studio/electron/src/main/loggy/forward.ts). The context object is
  // deliberately omitted — main pretty-prints objects across several lines and
  // the record is contracted to be ONE line.
  forwardLog({ source: ANALYTICS_START_FAILURE_SOURCE, level: 'error', message: line });

  if (options.retry !== undefined && !isElectronApp()) {
    const schedule = options.schedule ?? ((callback, delayMs) => setTimeout(callback, delayMs));
    schedule(() => retryAnalyticsStart(incidentId, options.retry!), ANALYTICS_START_RETRY_DELAY_MS);
  }

  return { incidentId, line };
}

// The one retry. Success sends the only PostHog row this incident will ever
// have; failure writes a second console line with the SAME id and stops.
function retryAnalyticsStart(incidentId: string, retry: () => PostHog): void {
  try {
    retry();
  } catch (error) {
    console.error(
      `[PostHog] ${ANALYTICS_START_FAILED_LINE_PREFIX} retry failed ` +
        `${COMMON_PROPERTIES.INCIDENT_ID}=${incidentId} error=${describeError(error)}`
    );
    return;
  }

  PostHogManager.getInstance().capture(EVENT_NAMES.ANALYTICS_START_FAILED, {
    [COMMON_PROPERTIES.INCIDENT_ID]: incidentId
  });
  console.info(
    `[PostHog] ${ANALYTICS_START_FAILED_LINE_PREFIX} retry succeeded ` +
      `${COMMON_PROPERTIES.INCIDENT_ID}=${incidentId}; requested ${EVENT_NAMES.ANALYTICS_START_FAILED} capture`
  );
}
